'use client'

import { Container } from '@/shared/components/Container'
import { motion } from 'framer-motion'
import Image from 'next/image'
import twelveApostlesBackground from '@/images/localized/twelve-apostles.png'
import Counter from '@/shared/components/framer/Counter'
import React, { Fragment } from 'react'
import SlowRevealSpan from './SlowRevealSpan'

export default function ConsultationStatisticsVic() {
  return (
    <Container className="mt-24">
      <motion.div
        variants={{
          start: { opacity: 0, y: 20 },
          end: { opacity: 1, y: 0, transition: { staggerChildren: 0.02 } },
        }}
        transition={{
          delay: 1,
          ease: 'easeOut',
        }}
        initial="start"
        whileInView="end"
        viewport={{ once: true, amount: 'all' }}
        className="relative mx-auto max-w-4xl overflow-hidden rounded-2xl border border-gray-300 bg-gray-100 px-6 py-4 shadow-lg shadow-black/15 md:px-12 md:py-8 xl:px-16 xl:py-12"
      >
        <div className="absolute left-1/2 top-1/2 h-[100%] w-[100%] -translate-x-1/2 -translate-y-1/2 opacity-15">
          <Image
            src={twelveApostlesBackground}
            alt="Twelve Apostles in the background"
            className="object-cover"
            fill
          />
        </div>
        <motion.div className="overflow-hidden">
          <motion.h2
            className="font-bitter text-lg font-medium text-gray-900 md:text-xl xl:text-2xl"
            variants={{
              start: { y: 100 },
              end: { y: 0 },
            }}
            transition={{ ease: 'easeOut', duration: 0.5 }}
          >
            We all need doctors
          </motion.h2>
        </motion.div>
        <p className="text-md mt-2 max-w-2xl font-sans font-light leading-relaxed text-gray-700 md:text-lg xl:mt-4 xl:text-xl">
          {'In fact, the average person sees their GP about'
            .split(' ')
            .map((text, idx) => (
              <Fragment key={`0-${idx}`}>
                <SlowRevealSpan>{text}</SlowRevealSpan>{' '}
              </Fragment>
            ))}
          <span className="relative inline-flex overflow-hidden font-bold">
            <motion.span
              className="inline-block"
              variants={{
                start: { y: 100 },
                end: {
                  y: 0,
                  transition: { staggerChildren: 0.02, duration: 0.4 },
                },
              }}
              transition={{
                delay: 1,
                ease: 'easeOut',
              }}
              initial="start"
              animate="end"
            >
              <motion.span
                className="absolute bottom-0.5 left-0 block h-1.5 w-full bg-rose-400 xl:bottom-1.5 xl:h-2"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ ease: 'easeOut', duration: 0.3, delay: 0.2 }}
              ></motion.span>

              {'7 times a year'.split(' ').map((text, idx) => (
                <span className="relative" key={`1-${idx}`}>
                  <SlowRevealSpan>{text}</SlowRevealSpan>{' '}
                </span>
              ))}
            </motion.span>
          </span>{' '}
          {`according to the Department of the Health and Aged Care. For Victoria, that adds up to a staggering`
            .split(' ')
            .map((text, idx) => (
              <Fragment key={`2-${idx}`}>
                <SlowRevealSpan key={idx}>{text}</SlowRevealSpan>{' '}
              </Fragment>
            ))}
          <Counter value={49000000} className="font-medium text-orange-500" />{' '}
          {'consultations annually. But here’s the frustrating part, getting an appointment can take up to'
            .split(' ')
            .map((text, idx) => (
              <Fragment key={`3-${idx}`}>
                <SlowRevealSpan key={idx}>{text}</SlowRevealSpan>{' '}
              </Fragment>
            ))}
          <SlowRevealSpan className="font-medium text-red-600">
            3
          </SlowRevealSpan>{' '}
          <SlowRevealSpan className="font-medium text-red-600">
            weeks
          </SlowRevealSpan>{' '}
          {'and what should be a quick 6-20 minute consultation turns into a time-consuming ordeal with travel and waiting room time.'
            .split(' ')
            .map((text, idx) => (
              <Fragment key={`4-${idx}`}>
                <SlowRevealSpan key={idx}>{text}</SlowRevealSpan>{' '}
              </Fragment>
            ))}
        </p>
      </motion.div>
    </Container>
  )
}
